/**
 * voiceSilence.ts — decides when someone has finished speaking, so dictation can stop by itself (as Gemini Live,
 * ChatGPT voice and phone keyboards do). Pure: it is given the microphone level a few times a second and the time.
 *
 *  - The first moments set the room's background level; speech is anything clearly above it.
 *  - Short bumps (a click, a cough under a quarter of a second) do not count as speech.
 *  - Once speech has been heard, a pause of `silenceMs` ends the recording.
 *  - If nothing is ever heard within `noSpeechMs`, it gives up.
 */
export interface SilenceOptions {
  /** How long a pause after speech ends the recording. */
  silenceMs?: number;
  /** How long to wait for the first word before giving up. */
  noSpeechMs?: number;
  /** The quietest level that can count as speech (0 to 1, root-mean-square). */
  minLevel?: number;
}
export type SilenceVerdict = "continue" | "stop" | "give_up";

const CALIBRATE_MS = 500;
const MIN_SPEECH_MS = 250;

export function createSilenceDetector(options: SilenceOptions = {}) {
  const silenceMs = options.silenceMs ?? 1800;
  const noSpeechMs = options.noSpeechMs ?? 7000;
  const minLevel = options.minLevel ?? 0.015;
  let startedAt: number | null = null;
  let floorSum = 0;
  let floorCount = 0;
  let threshold = minLevel;
  let speechMs = 0;
  let lastFrameAt = 0;
  let lastSpeechAt = 0;
  let heard = false;

  return {
    /** Feed one reading. `level` is 0 to 1; `now` is a time in milliseconds. */
    push(level: number, now: number): SilenceVerdict {
      if (startedAt === null) {
        startedAt = now;
        lastFrameAt = now;
      }
      const dt = Math.max(0, now - lastFrameAt);
      lastFrameAt = now;
      if (now - startedAt < CALIBRATE_MS) {
        floorSum += level;
        floorCount += 1;
        threshold = Math.max(minLevel, (floorSum / floorCount) * 2.5);
        return "continue";
      }
      if (level > threshold) {
        speechMs += dt;
        lastSpeechAt = now;
        if (speechMs >= MIN_SPEECH_MS) heard = true;
      }
      if (heard && now - lastSpeechAt >= silenceMs) return "stop";
      if (!heard && now - startedAt >= noSpeechMs) return "give_up";
      return "continue";
    },
  };
}
